import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import cn from 'classnames';
import { Popover, GlobalToken } from 'antd';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';

import EPTierInfoIcon from '@/comp/common/EPTierInfoIcon';
import EPTierProtection from '@/comp/common/EPTierProtection';
import { ComponentTierName, interestedPackageEnum, SensorsPackage } from '@/constants/enums';
import { checkInfoLockTierIsVisible } from '@/utils/util-functions';
import { useStores } from '@/hooks/use-stores';
import { token } from '@/theme';
import KPISkeleton from '../KPIBox/KPISkeleton/KPISkeleton';

interface KPIBoxWithTargetOptionProps {
  title: string;
  value: string;
  unit: string | JSX.Element;
  targetValue?: string;
  tooltip?: string;
  icon?: JSX.Element;
}

const KPIBoxWithTargetOption = observer(
  ({ title, icon, value, unit, targetValue, tooltip }: KPIBoxWithTargetOptionProps) => {
    const { t } = useTranslation();
    const { stationStore } = useStores();
    const infoTierData = (
      <EPTierProtection componentName={ComponentTierName.CompressorView}>
        <span className="tier-span">
          <EPTierInfoIcon
            type={title as SensorsPackage}
            tooltipTitle={t('isCalculated', { metric: t(title) })}
            tooltipTextPart1={t('toRecord', {
              field: title.toLocaleLowerCase().includes('power') ? 'performance' : title,
            })}
            tooltipTextPart2={t('getSensorPackage')}
            emailRequestTypeOfInterestedPackage={interestedPackageEnum.SENSOR_PACKAGE}
            emailRequestButtonClicked={`${title} tooltip`}
            emailRequestWhichScreen={'SSC'}
          />
        </span>
      </EPTierProtection>
    );
    const isTierLockVisible = checkInfoLockTierIsVisible(
      ComponentTierName.CompressorView,
      title as SensorsPackage,
      stationStore,
    );
    const isPopoverVisible = !isTierLockVisible;
    return (
      <Container>
        <div className="title-container">
          <span className="icon">{icon}</span>
          <span className="title">{title}</span>
        </div>

        <KPISkeleton
          loading={stationStore.isLoading}
          style={{ marginTop: token.marginXS, height: '100%' }}
          withSliderSkeleton={false}
        >
          <div className="values-row">
            <div className={cn('value-container')}>
              <Popover
                placement="bottom"
                content={
                  isPopoverVisible
                    ? tooltip ?? t('Value may depend on multiple sensors and average values over time.')
                    : null
                }
              >
                <span className={cn('value', { bold: value })}>{!isNil(value) ? value : '_ _'}</span>
                <span className="unit">{unit}</span>
              </Popover>
              {!isNil(value) && infoTierData}
            </div>

            {targetValue && (
              <div className={cn('target-container')}>
                <div className="target-title">{t('TARGET')}</div>
                <div className="target-value-container">
                  <span className={cn('target-value', { bold: targetValue })}>{targetValue ? targetValue : '_ _'}</span>
                  <span className="unit"> {unit}</span>
                </div>
              </div>
            )}
          </div>
        </KPISkeleton>
      </Container>
    );
  },
);

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  .title-container {
    display: flex;
    align-items: center;
    justify-content: left;

    .icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .title {
      margin-left: ${({ theme: { token } }) => token.marginXS}px;
      font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      color: ${({ theme: { token } }) => token.colorTextTertiary};
      padding-top: ${({ theme: { token } }) => token.paddingXXS}px;
      text-transform: uppercase;
    }
  }

  .values-row {
    display: flex;
    align-items: center;
    margin-top: ${({ theme: { token } }) => token.marginXXS}px;
  }

  .value-container {
    display: flex;

    .tier-span-wrapper {
      display: flex;
      align-items: baseline;
      margin-top: ${({ theme: { token } }) => token.marginXXS}px;
      font-size: ${({ theme: { token } }) => token.fontSize}px;
      margin-left: ${({ theme: { token } }) => token.marginXXS}px;
    }

    &:not(:only-child) {
      margin-right: ${({ theme: { token } }) => token.marginMD}px;
    }

    .tier-span {
      margin-left: ${({ theme: { token } }) => token.marginXXS}px;
      margin-top: ${({ theme: { token } }) => token.margin}px;
    }

    .value {
      font-size: ${({ theme: { token } }) => token.fontSizeHeading2}px;
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      color: ${({ theme: { token } }) => token.colorText};
      margin-right: ${({ theme: { token } }) => token.marginXXS}px;
    }

    .unit {
      font-size: ${({ theme: { token } }) => token.fontSize}px;
      color: ${({ theme: { token } }) => token.colorTextSecondary};
      margin-left: ${({ theme: { token } }) => token.marginXXS}px;
    }
  }

  .target-container {
    display: flex;
    flex-direction: column;
    align-items: baseline;

    .target-title {
      font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      color: ${({ theme: { token } }) => token.colorTextSecondary};
    }

    .target-value-container {
      .target-value {
        font-size: ${({ theme: { token } }) => token.fontSize}px;
        color: ${({ theme: { token } }) => token.colorTextSecondary};
      }

      .unit {
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        color: ${({ theme: { token } }) => token.colorTextSecondary};
        margin-left: ${({ theme: { token } }) => token.marginXXS}px;
      }
    }
  }
`;

export default KPIBoxWithTargetOption;
